import { apiFetch } from './client';
import type { ExistenciaPorUbicacion, Periodo, Ubicacion, Venta } from './tienda.api';

export interface FilaDeSupervision {
  userId: string | null;
  nombre: string;
  recibeChats: boolean;
  chatsAbiertos: number;
  sinContestar: number;
  esperaMaximaMin: number | null;
  recibidos: number;
  atendidos: number;
  primeraRespuestaMin: number | null;
  ventasConfirmadas: number;
  ventasEnBorrador: number;
  totalVendido: number;
  /** En pausa AHORA (almuerzo, atendiendo en la tienda…). */
  pausa: { motivo: string; etiqueta: string; desde: string; apoyo: boolean } | null;
  minutosEnPausa: number;
}

export type OrigenDeVenta = 'nuevaPorAnuncio' | 'carteraPorAnuncio' | 'nuevaQueRepite' | 'carteraSola' | 'nuevaSinAnuncio';

export interface Supervision {
  periodo: Periodo;
  desde: string;
  hasta: string;
  minutosParaContestar: number;
  filas: FilaDeSupervision[];
  origen: { chatsNuevos: number; porAnuncio: number; referidos: number; directos: number };
  atribucion: Record<OrigenDeVenta, { ventas: number; total: number }>;
}

// ─── Pausas (reunión del 12-oct) ─────────────────────────────────────────────

export type MotivoDePausa = 'almuerzo' | 'tienda' | 'otra';
export interface PausaAbierta {
  userId: string;
  nombre: string;
  motivo: MotivoDePausa;
  etiqueta: string;
  desde: string;
  apoyo: boolean;
}

export function pausas(): Promise<{ mia: PausaAbierta | null; abiertas: PausaAbierta[] }> {
  return apiFetch('/api/supervision/pausas');
}
export function empezarPausa(motivo: MotivoDePausa, apoyo = true) {
  return apiFetch('/api/supervision/pausa', { method: 'POST', body: { motivo, apoyo } });
}
export function terminarPausa(userId?: string) {
  return apiFetch<{ minutos: number }>('/api/supervision/pausa/terminar', { method: 'POST', body: userId ? { userId } : {} });
}

// ─── La ventanita: reportar un error o pedir un cambio ──────────────────────

export interface PedidoDeCambio {
  id: string;
  codigo: string;
  nombre: string;
  tipo: 'problema' | 'cambio' | null;
  titulo: string;
  pantalla: string | null;
  quePasa: string;
  urgencia: 'frena' | 'molesta' | 'mejora';
  estado: 'NUEVO' | 'EN_ANALISIS' | 'ACEPTADO' | 'RECHAZADO' | 'HECHO';
  respuesta: string | null;
  createdAt: string;
}
export function pedidosDeCambio(): Promise<{ items: PedidoDeCambio[]; puedeContestar: boolean }> {
  return apiFetch('/api/pedidos-de-cambio');
}
export function pedirCambio(body: { tipo: 'problema' | 'cambio'; quePasa: string; pantalla?: string; urgencia?: PedidoDeCambio['urgencia'] }) {
  return apiFetch<{ id: string; codigo: string }>('/api/pedidos-de-cambio', { method: 'POST', body });
}
export function contestarPedido(id: string, body: { estado?: PedidoDeCambio['estado']; respuesta?: string | null }) {
  return apiFetch<{ guardado: boolean }>(`/api/pedidos-de-cambio/${id}`, { method: 'PATCH', body });
}

export interface ChatEsperando {
  conversationId: string;
  cliente: string;
  asesora: string | null;
  minutos: number;
}

export function supervision(periodo: Periodo): Promise<Supervision> {
  return apiFetch(`/api/supervision?periodo=${periodo}`);
}

export function esperando(): Promise<{ minutosParaContestar: number; chats: ChatEsperando[] }> {
  return apiFetch('/api/supervision/esperando');
}

// ─── Pasar una venta desde el chat ───────────────────────────────────────────

export interface ProductoParaVender {
  id: string;
  name: string;
  sku: string | null;
  price: number;
  variants: Array<{
    id: string;
    label: string;
    stock: number;
    price: number | null;
    /** Lo que hay en el almacén y en cada tienda (por id). La tienda que no aparece tiene cero. */
    enAlmacen: number;
    porTienda: Record<string, number>;
  }>;
}

export function productosParaVender(buscar: string): Promise<ProductoParaVender[]> {
  return apiFetch(`/api/tienda/ventas/del-chat/productos?buscar=${encodeURIComponent(buscar)}`);
}

export function ventasDelChat(conversationId: string): Promise<Venta[]> {
  return apiFetch(`/api/tienda/ventas/del-chat/chat/${conversationId}`);
}

export function ventaDelChat(id: string): Promise<Venta> {
  return apiFetch(`/api/tienda/ventas/del-chat/venta/${id}`);
}

/** Existencias de solo lectura para la vendedora: su tienda primero. */
export function existenciasParaVer(): Promise<{
  miTienda: string | null;
  lugares: Ubicacion[];
  piezas: ExistenciaPorUbicacion[];
}> {
  return apiFetch('/api/tienda/ventas/del-chat/existencias');
}

/** El almacén y las tiendas: de dónde puede salir la venta. */
export function lugaresParaVender(): Promise<Ubicacion[]> {
  return apiFetch('/api/tienda/ventas/del-chat/lugares');
}

export function pasarVenta(body: {
  conversationId: string;
  lineas: Array<{ productVariantId: string; cantidad: number; descuento?: number }>;
  nombreCliente?: string | null;
  formaDePago?: string | null;
  descuento?: number;
  envio?: number;
  direccion?: string | null;
  notas?: string | null;
  /** Lo que pagó hoy: todo, nada o un monto (el anticipo). */
  pago?: 'todo' | 'nada' | number;
  /** De qué lugar sale. Vacío = el almacén. */
  ubicacionId?: string | null;
  envioModo?: 'tienda' | 'mensajero' | 'transportista' | null;
  envioEmpresa?: string | null;
  envioDestino?: string | null;
  costoMensajero?: number;
  costoTransportista?: number;
}): Promise<{ venta: Venta; confirmada: boolean; motivo: string | null }> {
  return apiFetch('/api/tienda/ventas/del-chat', { method: 'POST', body });
}

// ─── Mis ventas y «vino referido» ────────────────────────────────────────────

export interface MisVentas {
  desde: string;
  hasta: string;
  resumen: { confirmadas: number; enBorrador: number; total: number; piezas: number };
  ventas: Venta[];
}

/** Las ventas que pasó quien pregunta. Nunca las de otra persona. */
export function misVentas(periodo: Periodo): Promise<MisVentas> {
  return apiFetch(`/api/tienda/ventas/del-chat/mias?periodo=${periodo}`);
}

export function marcarReferido(conversationId: string, referido: boolean): Promise<{ referido: boolean }> {
  return apiFetch('/api/supervision/referido', { method: 'POST', body: { conversationId, referido } });
}
